import { DatePipe, Time } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, Subject, Subscription, catchError, debounceTime, switchMap, tap } from 'rxjs';
import { SESSION_ID } from 'src/app/constants/authentication.constants';
import { splitFilename } from 'src/app/helpers/file-helpers';
import { CaseModel } from 'src/app/models/case.model';
import { DocumentModel, DocumentTableColumns } from 'src/app/models/document.model';
import { FileMenuActions } from 'src/app/models/file-menu.model';
import { DocumentDataService } from 'src/app/services/document-data.service';
import { DocumentService } from 'src/app/services/document.service';
import { ToastService } from 'src/app/services/toast.service';
import { WindowService } from 'src/app/services/window.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'tx-client-case-documents',
  templateUrl: './client-case-documents.component.html',
  styleUrls: ['./client-case-documents.component.scss'],
})
export class ClientCaseDocumentsComponent implements OnInit, OnChanges {
  @Input() selectedContactId: string | undefined;
  @Input() selectedCaseId: string | CaseModel | undefined;
  @Input() isPdf: boolean = false;
  @Input() isDirty: boolean = false;
  @Input() existingDocument: string | undefined = '';
  @Input() allowOpen = true;
  @Input() fileMenuAction: string = '';
  @Output() openDocument: EventEmitter<DocumentModel | null> = new EventEmitter();
  @Output() openInNewTab: EventEmitter<DocumentModel | null> = new EventEmitter();
  @ViewChild('openInNewTabForm') openInNewTabForm!: ElementRef;
  public documents: Array<DocumentModel> = [];
  public selectedDocument: DocumentModel | null;
  public submitUrl: string;
  public sessionId = SESSION_ID;
  public isCaseSelected = false;
  public loading = false;
  public columns = DocumentTableColumns;
  public loadDocumentsForCase: Subject<CaseModel> = new Subject<CaseModel>();
  public loadDocumentsObservable: Subscription;
  private defaultSubmitUrl: string;
  private pdfSubmitUrl: string;
  constructor(
    private toastService: ToastService,
    private documentService: DocumentService,
    private documentDataService: DocumentDataService,
    private datePipe: DatePipe,
    private windowService: WindowService,
    private ref: ChangeDetectorRef
  ) {
    this.documents = new Array<DocumentModel>();
    this.selectedDocument = null;
    this.defaultSubmitUrl = environment.endpoints.apiUrl + '/document/opendocumentinnewsession';
    this.pdfSubmitUrl = environment.endpoints.apiUrl + '/pdfdocument/opendocumentinnewsession';
    this.submitUrl = this.defaultSubmitUrl;

    this.loadDocumentsObservable = this.loadDocumentsForCase
      .pipe(
        debounceTime(25),
        tap(() => {
          this.isCaseSelected = true;
          this.selectedDocument = null;
          this.loading = true;
        }),
        switchMap((cas) => this.documentService.getDocumentList(cas)),
        tap((res) => {
          if (res && res.documents && res.documents.length > 0) {
            this.documents = res.documents;
          } else {
            this.documents = [];
          }
          this.loading = false;
        }),
        catchError((err) => {
          this.loading = false;
          return [];
        })
      )
      .subscribe({});
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCaseId'] && changes['selectedCaseId'].currentValue) {
      this.selectedCaseId = changes['selectedCaseId'].currentValue as CaseModel;
      this.getDocumentsForCase(changes['selectedCaseId'].currentValue as CaseModel);
    }
  }

  ngDestroy(): void {
    if (this.loadDocumentsObservable) {
      this.loadDocumentsObservable.unsubscribe();
    }
  }

  ngAfterViewInit(): void {}

  getDocumentsForCase(cas: CaseModel) {
    this.loadDocumentsForCase.next(cas);
  }

  onDocumentSelected(doc: DocumentModel) {
    this.selectedDocument = doc;

    // set hidden form values for open in new tab functionality
    const dcmIdInput = document.getElementById('dcmID') as HTMLInputElement;
    if (dcmIdInput) {
      dcmIdInput.value = this.selectedDocument?.dcmID ?? '';
    }

    const htmlSessionId = document.getElementById(SESSION_ID) as HTMLInputElement;
    if (htmlSessionId) {
      htmlSessionId.value = this.windowService.getSessionIdFromWindow() ?? '';
    }
  }

  onOpenClicked(doc: DocumentModel) {
    this.onDocumentSelected(doc);
    this.openDocument.emit(doc);
  }

  onOpenInNewTabClicked(doc: DocumentModel) {
    if (doc.openInAnotherSession || this.fileMenuAction !== FileMenuActions.Open || !doc.filename) {
      return;
    }

    const extension = splitFilename(doc.filename).extension;
    this.submitUrl = extension.toLowerCase() === 'pdf' ? this.pdfSubmitUrl : this.defaultSubmitUrl;
    // If we have the pdf component open and want to open a non-pdf file, we open it in a new tab.
    // Same when we have the TXTextControl component open and want to open a PDF file.
    // This is to simplify things, as we don't have the routing fully set up with documentID etc. Do we want to change this in future?
    this.ref.detectChanges();
    const openInNewTab: boolean =
      (this.isPdf && this.submitUrl === this.defaultSubmitUrl) || (!this.isPdf && this.submitUrl === this.pdfSubmitUrl);

    if (this.existingDocument || this.isDirty || openInNewTab) {
      this.onDocumentSelected(doc);
      this.openInNewTabForm.nativeElement.submit();
      this.openInNewTab.emit(doc);
    } else {
      this.onDocumentSelected(doc);
      this.openDocument.emit(doc);
    }
  }

  onSortClicked(column: string) {
    switch (column) {
      case this.columns.FileName:
        break;
      case this.columns.CreatedBy:
        break;
      case this.columns.CreatedDate:
        break;
      case this.columns.ModifiedBy:
        break;
      case this.columns.ModifiedDate:
        break;
      default:
        break;
    }
  }

  getDate(d: DocumentModel) {
    const date: Date | undefined = d.modifiedDate ?? d.date;
    if (date != undefined) {
      return this.datePipe.transform(date, 'MM/dd/yyyy');
    }

    return '';
  }

  getTime(d: DocumentModel) {
    const time: Time | undefined = d.modifiedTime ?? d.time;
    if (time != undefined) {
      const timeString = time.toString();
      const [hours, minutes, seconds] = timeString.split(':');
      let date = new Date();
      date.setHours(parseInt(hours));
      date.setMinutes(parseInt(minutes));

      return this.datePipe.transform(date, 'h:mm a');
    }

    return '';
  }
}
